import { getFirestore, collection, doc, addDoc, serverTimestamp, query, orderBy, getDocs, Timestamp, getDoc, updateDoc } from "firebase/firestore";
import { app } from "../firebaseConfig";

const db = getFirestore(app);
const EVENT_ID = "250708_purplekiss"; // 필요시 동적으로 변경

// 점수 저장 함수
export async function submitScore({ nickname, phone, score, maxCombo, roundResult, privacyAgreed }) {
  const scoresRef = collection(db, "rhythm_events", EVENT_ID, "scores");
  const docRef = await addDoc(scoresRef, {
    nickname,
    phone,
    score,
    maxCombo,
    roundResult,
    privacyAgreed: !!privacyAgreed,
    submittedAt: toKstIso(Timestamp.now()),
  });

  return docRef.id; // 생성된 문서의 ID 반환
}

// 랭킹 조회 함수 (phone 조합별 최고 점수, 상위 10명)
export async function getTopRanking() {
  const scoresRef = collection(db, "rhythm_events", EVENT_ID, "scores");
  const q = query(scoresRef, orderBy("score", "desc"), orderBy("maxCombo", "desc"), orderBy("submittedAt", "asc"));
  const snapshot = await getDocs(q);

  // 사용자별(phone) 최고 점수만 추출
  const userBest = {};
  snapshot.forEach(doc => {
    const data = doc.data();
    const key = data.phone; // 핸드폰 번호로만 중복 제거
    //console.log('data', data);
    //console.log('key', key);
    // 점수, 콤보, 제출시각 모두 비교
    if (
      !userBest[key] ||
      data.score > userBest[key].score ||
      (data.score === userBest[key].score && data.maxCombo > userBest[key].maxCombo) ||
      (data.score === userBest[key].score && data.maxCombo === userBest[key].maxCombo && data.submittedAt < userBest[key].submittedAt)
    ) {
      userBest[key] = { ...data };
    }
  });

  // 정렬: 점수 > 콤보 > 제출시각
  return Object.values(userBest)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.maxCombo !== a.maxCombo) return b.maxCombo - a.maxCombo;
      return a.submittedAt?.seconds - b.submittedAt?.seconds;
    })
    //.slice(0, 10); // 랭킹 10명 제한
} 

// 룰렛 상품 정보 조회 함수
export async function getRoulettePrizes() {
  try {
    const rouletteRef = doc(db, "rhythm_events", EVENT_ID, "roulette", "info");
    const docSnap = await getDoc(rouletteRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } 
    return null;
  } catch (error) {
    //console.error("룰렛 상품 정보 조회 실패:", error);
    throw error;
  }
}

// 룰렛 당첨 결과 저장 함수
export async function saveRouletteResult({ nickname, phone, prizeName, userDocId }) {
  try {
    const resultRef = collection(db, "rhythm_events", EVENT_ID, "roulette_result");
    await addDoc(resultRef, {
      nickname,
      phone,
      prizeName,
      timestamp: toKstIso(Timestamp.now()),
      userDocId // 랭킹에 저장된 유저의 문서 ID
    });

    // 상품별 당첨자 수 체크 및 확률 업데이트
    await checkAndUpdatePrizeProbability(prizeName);
  } catch (error) {
    //console.error("룰렛 당첨 결과 저장 실패:", error);
    throw error;
  }
}

// 상품별 당첨자 수 체크 및 확률 업데이트 함수
export async function checkAndUpdatePrizeProbability(prizeName) {
  try {
    // 1. 룰렛 정보 조회
    const rouletteRef = doc(db, "rhythm_events", EVENT_ID, "roulette", "info");
    const rouletteDoc = await getDoc(rouletteRef);
    
    if (!rouletteDoc.exists()) {
      //console.error("룰렛 정보를 찾을 수 없습니다.");
      return;
    }

    const rouletteData = rouletteDoc.data();
    const prizes = rouletteData.prizes;

    // 2. 해당 상품의 수량 제한 찾기
    const targetPrize = prizes.find(prize => prize.option === prizeName && !prize.isLose);
    if (!targetPrize || !targetPrize.maxQuantity) {
      // 수량 제한이 없으면 업데이트 불필요
      return;
    }

    // 3. 해당 상품의 현재 당첨자 수 조회
    const resultRef = collection(db, "rhythm_events", EVENT_ID, "roulette_result");
    const snapshot = await getDocs(resultRef);
    
    let currentWinners = 0;
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.prizeName === prizeName) {
        currentWinners++;
      }
    });

    // 4. 수량 제한에 도달했는지 확인
    if (currentWinners >= targetPrize.maxQuantity) {
      //console.log(`상품 "${prizeName}"의 수량 제한(${targetPrize.maxQuantity}개)에 도달했습니다. 확률을 0으로 업데이트합니다.`);
      
      // 5. 해당 상품의 확률을 0으로 업데이트
      const updatedPrizes = prizes.map(prize => {
        if (prize.option === prizeName && !prize.isLose) {
          return { ...prize, probability: 0 };
        }
        return prize;
      });

      // 6. 룰렛 정보 업데이트
      await updateDoc(rouletteRef, {
        prizes: updatedPrizes
      });

      //console.log(`상품 "${prizeName}"의 확률이 0으로 업데이트되었습니다.`);
    }
  } catch (error) {
    //console.error("상품 확률 업데이트 실패:", error);
    // 확률 업데이트 실패해도 당첨 결과 저장은 성공했으므로 에러를 던지지 않음
  }
}

// 사용자별 최고 점수 문서 ID 조회 함수
export async function getUserBestScoreDocId(nickname, phone) {
  try {
    const scoresRef = collection(db, "rhythm_events", EVENT_ID, "scores");
    const q = query(scoresRef, orderBy("score", "desc"), orderBy("maxCombo", "desc"), orderBy("submittedAt", "asc"));
    const snapshot = await getDocs(q);

    // 사용자별(nickname+phone) 최고 점수 문서 찾기
    let userBestDoc = null;
    let userBestScore = 0;
    let userBestCombo = 0;
    let userBestTime = null;

    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.nickname === nickname && data.phone === phone) {
        // 점수, 콤보, 제출시각 모두 비교
        if (
          !userBestDoc ||
          data.score > userBestScore ||
          (data.score === userBestScore && data.maxCombo > userBestCombo) ||
          (data.score === userBestScore && data.maxCombo === userBestCombo && data.submittedAt < userBestTime)
        ) {
          userBestDoc = doc;
          userBestScore = data.score;
          userBestCombo = data.maxCombo;
          userBestTime = data.submittedAt;
        }
      }
    });

    return userBestDoc ? userBestDoc.id : null;
  } catch (error) {
    //console.error("사용자 최고 점수 문서 ID 조회 실패:", error);
    throw error;
  }
}

// 당첨 목록 조회 함수
export async function getWinnerList() {
  try {
    const resultRef = collection(db, "rhythm_events", EVENT_ID, "roulette_result");
    const snapshot = await getDocs(resultRef);
    
    const winners = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      winners.push({
        id: doc.id,
        nickname: data.nickname,
        phone: data.phone,
        prizeName: data.prizeName,
        timestamp: data.timestamp
      });
    });
    
    return winners;
  } catch (error) {
    //console.error("당첨 목록 조회 실패:", error);
    throw error;
  }
}

// 특정 사용자의 룰렛 당첨 여부 확인 함수
export async function hasUserWonRoulette(phone) {
  try {
    const resultRef = collection(db, "rhythm_events", EVENT_ID, "roulette_result");
    const snapshot = await getDocs(resultRef);
    
    // 해당 전화번호로 당첨 기록이 있는지 확인
    const hasWon = snapshot.docs.some(doc => {
      const data = doc.data();
      return data.phone === phone;
    });
    
    return hasWon;
  } catch (error) {
    //console.error("사용자 당첨 여부 확인 실패:", error);
    throw error;
  }
}

/**
 * Firestore Timestamp 또는 JS Date, 밀리초 숫자를 KST 기준 ISO 문자열(YYYY-MM-DDTHH:mm:ss)로 변환
 * @param {import("firebase/firestore").Timestamp|Date|number} ts
 * @returns {string} "2025-07-15T19:45:30"
 */
export const toKstIso = ts => {
  // Firestore Timestamp 객체라면 toMillis(), Date 객체라면 getTime(), 숫자(밀리초)라면 그대로
  const ms = ts?.toMillis
    ? ts.toMillis()
    : ts instanceof Date
      ? ts.getTime()
      : ts;
  // KST = UTC+9h
  const kstMs = ms + 9 * 60 * 60 * 1000;
  // toISOString() → "YYYY-MM-DDTHH:mm:ss.sssZ", slice으로 밀리초·Z 제거
  return new Date(kstMs).toISOString();
};

// 사용 예 (Firestore Timestamp라 가정)
// const submittedAt = docSnap.data().submittedAt;
// console.log(toKstIso(submittedAt));  // "2025-07-15T19:45:30"